import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, Mail, Shield, Users, ArrowRight, Crown, Info, X, LogIn } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import CoachBSpeak from '../components/CoachBSpeak';

// L'Chaim Color Palette
const COLORS = {
  darkBlue: '#0a1628',
  mediumBlue: '#1a3a5c',
  cyan: '#00d4ff',
  red: '#dc2626',
  parchment: '#F5F5DC',
  white: '#ffffff',
  gray: '#9ca3af',
};

// Unified tier set — matches /academy/lchaim/enroll
const TIERS = [
  {
    id: 'lchaim_sentry',
    name: 'The Sentry',
    price: 5,
    icon: Mail,
    tagline: 'Daily Guidance',
    description: 'Daily wisdom delivered to your inbox',
    features: [
      'Daily email delivery',
      '350 days of content',
      'Five Pillars curriculum',
      'Scripture & reflections',
    ],
    cta: 'Begin The Journey',
  },
  {
    id: 'lchaim_guardian',
    name: 'The Guardian',
    price: 19,
    icon: Shield,
    tagline: 'Full Dashboard',
    description: 'Interactive journey experience',
    features: [
      "Everything in The Sentry",
      'Interactive Dashboard',
      "Sentry's Log (journaling)",
      'Ancestral Vault uploads',
      'Progress tracking',
    ],
    cta: 'Go Guardian',
    popular: true,
  },
  {
    id: 'lchaim_order',
    name: 'The Order',
    price: 24,
    icon: Users,
    tagline: 'The Tandem Experience',
    description: 'Parent + Student linked accounts',
    features: [
      'Everything in The Guardian',
      'Parent-Student linking',
      "Coach's Companion",
      'Real-time sync',
    ],
    cta: 'Join The Order',
  },
];

const PILLARS = [
  {
    name: 'Respect',
    num: 1,
    color: '#00d4ff',
    short: 'Honor self, family, and the room.',
    teach: "We teach the young man to look elders in the eye, to handle his name with care, and to give weight to spaces that came before him. Respect is the first language he learns to speak — to his mother, his coaches, his classmates, and ultimately himself. We define respect not as fear, but as recognition: that every person in the room carries a story he hasn't read yet."
  },
  {
    name: 'Responsibility',
    num: 2,
    color: '#fbbf24',
    short: 'The buck stops with you.',
    teach: "Responsibility is the daily practice of owning your impact — on your body, your schoolwork, your team, your home. We teach him that no one is coming to save him from his own choices, and that being trustworthy is the foundation every other man-skill is built on. He learns to track his commitments, repair what he broke, and show up before he's reminded."
  },
  {
    name: 'Resilience',
    num: 3,
    color: '#dc2626',
    short: 'You will fall. You will rise.',
    teach: "We don't teach toughness — we teach recovery. He'll learn how to lose without quitting, how to be embarrassed without being defeated, and how to wake up after a hard day and try again. Resilience is a muscle, and we train it weekly: through physical reps, academic setbacks, social bruises, and the small daily fires every boy has to put out on his way to manhood."
  },
  {
    name: 'Reflection',
    num: 4,
    color: '#10b981',
    short: 'Examine the day. Adjust the man.',
    teach: "Every Sunday, every week's end, every milestone — he sits with himself. We give him the journaling rhythm, the prayer cadence, and the questions a young man should ask before bed: What did I build today? Who did I serve? What did I avoid? Reflection is how he turns experience into wisdom instead of letting it just become noise."
  },
  {
    name: 'Restraint',
    num: 5,
    color: '#a855f7',
    short: 'Power under control.',
    teach: "Strength without restraint is destruction. We teach the young man that the most powerful word in his vocabulary is 'not yet' — that withholding the first reaction, the wrong word, the impulse buy, the snap text, is what separates a kid from a man. Restraint is the bridge between his desires and his integrity, and we walk him across it on purpose."
  },
];

// Logo
const LChaimLogo = ({ size = 'large' }) => {
  const fontSize = size === 'large' ? 'text-6xl md:text-8xl' : size === 'medium' ? 'text-4xl md:text-5xl' : 'text-2xl';
  return (
    <h1 className={`${fontSize} font-black tracking-tight`}>
      <span style={{ color: COLORS.white }}>L'</span>
      <span style={{ color: COLORS.cyan }}>CHA</span>
      <span style={{ color: COLORS.red }}>IM</span>
    </h1>
  );
};

const LChaimLanding = () => {
  const navigate = useNavigate();
  const [activePillar, setActivePillar] = useState(null);

  const handleEnroll = (tierId) => {
    navigate(`/academy/lchaim/enroll?tier=${tierId}`);
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen" style={{ backgroundColor: COLORS.darkBlue }}>
        {/* Background grid */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(${COLORS.cyan}08 1px, transparent 1px), linear-gradient(90deg, ${COLORS.cyan}08 1px, transparent 1px), radial-gradient(ellipse at 50% 0%, ${COLORS.mediumBlue}40 0%, transparent 70%)`,
            backgroundSize: '40px 40px, 40px 40px, 100% 100%',
          }}
        />

        {/* Top-right Sign In link — sits above the global header */}
        <div className="fixed top-20 right-4 z-[200] md:top-24 md:right-6">
          <button
            onClick={() => navigate('/academy/lchaim/login')}
            className="flex items-center gap-2 px-4 py-2 rounded border text-sm font-bold uppercase tracking-wider transition-colors backdrop-blur-sm shadow-lg"
            style={{ borderColor: COLORS.cyan, color: COLORS.cyan, backgroundColor: 'rgba(10,22,40,0.85)' }}
            data-testid="lchaim-signin-btn"
          >
            <LogIn className="w-4 h-4" />
            Sign In
          </button>
        </div>

        {/* Hero Section */}
        <section className="relative py-20 px-6">
          <div className="max-w-5xl mx-auto text-center relative z-10">
            {/* Created by PRSVR Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 border"
              style={{ borderColor: `${COLORS.cyan}50`, backgroundColor: 'rgba(0,212,255,0.05)' }}
            >
              <Crown className="w-4 h-4" style={{ color: COLORS.cyan }} />
              <span className="text-sm font-bold uppercase tracking-wider" style={{ color: COLORS.cyan }}>
                Created by PRSVR
              </span>
            </div>

            <LChaimLogo size="large" />

            <p className="text-xl md:text-2xl mt-4 mb-2 font-light" style={{ color: COLORS.cyan }}>
              The 13th Year Journey
            </p>

            {/* New quote */}
            <p className="text-lg md:text-xl mb-2 max-w-2xl mx-auto" style={{ color: COLORS.white }}>
              Because a boy doesn't become a man, he's molded into one.
            </p>
            <p className="text-2xl md:text-3xl font-black mb-12 max-w-2xl mx-auto" style={{ color: COLORS.white }}>
              Be intentional with the how.
            </p>

            <Button
              onClick={() => document.getElementById('tiers')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-6 text-lg font-bold uppercase tracking-wider"
              style={{ backgroundColor: COLORS.red, color: COLORS.white }}
              data-testid="hero-begin-journey-btn"
            >
              Begin The Journey <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          {/* Divider */}
          <div className="max-w-4xl mx-auto mt-16">
            <div className="h-px" style={{ background: `linear-gradient(90deg, transparent, ${COLORS.cyan}40, transparent)` }} />
          </div>
        </section>

        {/* The Five Pillars — clickable */}
        <section className="py-16 px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-3 uppercase tracking-wider" style={{ color: COLORS.white }}>
              The Five Pillars
            </h2>
            <p className="text-center mb-10 text-sm uppercase tracking-widest" style={{ color: COLORS.gray }}>
              Click any pillar to see what we teach
            </p>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 max-w-4xl mx-auto" data-testid="pillars-grid">
              {PILLARS.map((pillar) => (
                <button
                  key={pillar.name}
                  onClick={() => setActivePillar(pillar)}
                  className="group relative px-3 py-5 rounded border transition-all hover:scale-105 hover:-translate-y-1 text-left"
                  style={{
                    backgroundColor: `${COLORS.mediumBlue}60`,
                    borderColor: `${pillar.color}40`,
                  }}
                  data-testid={`pillar-${pillar.name.toLowerCase()}`}
                >
                  <div className="text-xs font-mono mb-1" style={{ color: pillar.color }}>
                    0{pillar.num}
                  </div>
                  <div className="font-black text-base md:text-lg uppercase tracking-tight" style={{ color: COLORS.white }}>
                    {pillar.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest mt-2 opacity-70 group-hover:opacity-100 transition-opacity" style={{ color: pillar.color }}>
                    Tap to read →
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Value Prop */}
        <section className="py-16 px-6 relative z-10" style={{ backgroundColor: `${COLORS.mediumBlue}40` }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-black mb-6 uppercase tracking-wider" style={{ color: COLORS.white }}>
              Your Son's Journey Starts Here
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: COLORS.white }}>
              L'Chaim provides the rhythm. For{' '}
              <span style={{ color: COLORS.cyan }} className="font-bold">50 weeks</span>,
              your son receives daily guidance built on the Five Pillars.
            </p>
          </div>
        </section>

        {/* Pricing Tiers */}
        <section id="tiers" className="py-20 px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-10 uppercase tracking-wider" style={{ color: COLORS.white }}>
              Choose Your Path
            </h2>

            <div className="grid md:grid-cols-3 gap-6 items-stretch">
              {TIERS.map((tier) => {
                const Icon = tier.icon;
                return (
                  <div
                    key={tier.id}
                    className="relative rounded-lg border overflow-hidden transition-all duration-300 hover:scale-[1.02] flex flex-col"
                    style={{
                      backgroundColor: `${COLORS.mediumBlue}80`,
                      borderColor: tier.popular ? COLORS.red : `${COLORS.cyan}30`,
                    }}
                    data-testid={`tier-card-${tier.id}`}
                  >
                    {tier.popular && (
                      <div
                        className="py-2 text-center font-bold text-xs uppercase tracking-wider"
                        style={{ backgroundColor: COLORS.red, color: COLORS.white }}
                      >
                        Most Popular
                      </div>
                    )}

                    {/* Card body — flex column with mt-auto CTA */}
                    <div className="p-6 flex flex-col flex-1">
                      {/* Header */}
                      <div className="text-center mb-6">
                        <div
                          className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: COLORS.darkBlue, border: `2px solid ${COLORS.cyan}` }}
                        >
                          <Icon className="w-7 h-7" style={{ color: COLORS.cyan }} />
                        </div>
                        <h3 className="text-xl font-black uppercase tracking-wider mb-1" style={{ color: COLORS.white }}>
                          {tier.name}
                        </h3>
                        <p className="text-xs uppercase tracking-wider" style={{ color: COLORS.cyan }}>
                          {tier.tagline}
                        </p>
                      </div>

                      {/* Price */}
                      <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-black" style={{ color: COLORS.white }}>
                            ${tier.price}
                          </span>
                          <span className="text-sm" style={{ color: COLORS.gray }}>/mo</span>
                        </div>
                        <p className="text-xs mt-1" style={{ color: COLORS.gray }}>
                          {tier.description}
                        </p>
                      </div>

                      {/* Features */}
                      <ul className="space-y-2 mb-6 min-h-[8rem]">
                        {tier.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: COLORS.cyan }} />
                            <span className="text-sm" style={{ color: COLORS.white }}>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA pinned to bottom — guarantees all 3 buttons align */}
                      <div className="mt-auto pt-2">
                        <Button
                          onClick={() => handleEnroll(tier.id)}
                          className="w-full py-5 font-bold uppercase tracking-wider"
                          style={{
                            backgroundColor: tier.popular ? COLORS.red : COLORS.cyan,
                            color: tier.popular ? COLORS.white : COLORS.darkBlue,
                          }}
                          data-testid={`tier-cta-${tier.id}`}
                        >
                          {tier.cta}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-20 px-6 relative z-10" style={{ backgroundColor: `${COLORS.mediumBlue}40` }}>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wider" style={{ color: COLORS.white }}>
              The Journey Awaits
            </h2>
            <p className="text-lg mb-8" style={{ color: COLORS.gray }}>
              350 days from now, your son will emerge transformed — not by accident, but by design.
            </p>
            <Button
              onClick={() => handleEnroll('lchaim_guardian')}
              className="px-10 py-6 text-lg font-bold uppercase tracking-wider"
              style={{ backgroundColor: COLORS.red, color: COLORS.white }}
            >
              Start The Journey Today
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-6 text-center relative z-10" style={{ backgroundColor: COLORS.darkBlue }}>
          <p className="text-sm" style={{ color: COLORS.gray }}>
            L'Chaim: The 13th Year Journey • By PRSVR
          </p>
        </footer>

        {/* Pillar definition dialog */}
        <Dialog open={!!activePillar} onOpenChange={(open) => !open && setActivePillar(null)}>
          <DialogContent
            className="max-w-2xl border-0"
            style={{ backgroundColor: COLORS.darkBlue, borderColor: activePillar?.color || COLORS.cyan, color: COLORS.white }}
          >
            {activePillar && (
              <>
                <DialogHeader>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-xs font-mono" style={{ color: activePillar.color }}>
                      PILLAR 0{activePillar.num}
                    </span>
                    <span className="h-px flex-1" style={{ backgroundColor: `${activePillar.color}30` }} />
                  </div>
                  <DialogTitle className="text-4xl font-black uppercase tracking-tight" style={{ color: COLORS.white }}>
                    {activePillar.name}
                  </DialogTitle>
                  <DialogDescription className="text-base font-bold" style={{ color: activePillar.color }}>
                    {activePillar.short}
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-2 text-base leading-relaxed" style={{ color: COLORS.white }}>
                  {activePillar.teach}
                </div>
                {/* Hear Coach B read this pillar aloud */}
                <div className="mt-4" data-testid="lchaim-pillar-voice">
                  <CoachBSpeak
                    text={`${activePillar.name}. ${activePillar.short}. ${activePillar.teach}`.slice(0, 480)}
                    label="Hear Coach B teach this"
                    variant="full"
                    accent={activePillar.color || '#c99a3d'}
                    testId="lchaim-landing-coach-b"
                  />
                </div>
                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={() => setActivePillar(null)}
                    className="font-bold uppercase tracking-wider"
                    style={{ backgroundColor: activePillar.color, color: COLORS.darkBlue }}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default LChaimLanding;
